import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { createBusinessesApi } from "../api";
import { Flex, Grid, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Avatar,
  Badge,
  Card,
  EmptyState,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { NotesPanel } from "@/modules/users/components/UserProfilePanels";
import type {
  BusinessMembershipsResponse,
  BusinessNoteDto,
  BusinessOverviewResponse,
} from "../types";
import { formatLabel } from "../labels";

function money(amounts: Record<string, number>): string {
  const entries = Object.entries(amounts).filter(([, v]) => v !== 0);
  if (entries.length === 0) return "â€”";
  return entries
    .map(([currency, amount]) => {
      try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
      } catch {
        return `${currency} ${amount.toFixed(2)}`;
      }
    })
    .join(" Â· ");
}

function when(iso: string | null | undefined): string {
  if (!iso) return "â€”";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "â€”"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function breakdown(counts: Record<string, number>): string {
  const parts = Object.entries(counts).map(([k, n]) => `${n} ${formatLabel(k)}`);
  return parts.length ? parts.join(", ") : "none";
}

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <Stack gap={4}>
        <Text role="caption" color="muted" as="span">
          {label}
        </Text>
        <Text role="h2" as="span">
          {value}
        </Text>
        {hint ? (
          <Text role="caption" color="secondary" as="span">
            {hint}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

/** Performance at a glance: supply (courts), demand (bookings), money in. */
export function BusinessStats({ overview }: { overview: BusinessOverviewResponse }) {
  const { supply, demand } = overview;
  const live = supply.resources_by_status.available ?? 0;
  return (
    <Stack gap={8}>
      <Grid columns="repeat(auto-fit, minmax(170px, 1fr))" gap={12}>
        <Stat
          label="Listings"
          value={supply.resources_total}
          hint={supply.resources_total ? `${live} bookable Â· ${breakdown(supply.resources_by_status)}` : "none created"}
        />
        <Stat
          label="Bookings"
          value={demand.bookings_total}
          hint={demand.bookings_total ? breakdown(demand.bookings_by_status) : "none yet"}
        />
        <Stat label="Last 30 days" value={demand.bookings_last_30_days} hint="bookings created" />
        <Stat label="Upcoming" value={demand.bookings_upcoming} hint="confirmed or pending" />
        <Stat label="Paid (gross)" value={money(demand.paid_by_currency)} hint="successful booking payments" />
        <Stat
          label="Open disputes"
          value={demand.open_disputes}
          hint={demand.last_booking_at ? `last booking ${when(demand.last_booking_at)}` : "no bookings"}
        />
      </Grid>
      {demand.truncated ? (
        <Text role="caption" color="muted" as="p">
          Counted from the latest 5,000 bookings.
        </Text>
      ) : null}
    </Stack>
  );
}

/** Sports: what the owner registered in Play versus what their listings actually offer. */
export function BusinessSportsCard({ overview }: { overview: BusinessOverviewResponse }) {
  const { registered, listed } = overview.sports;
  const unlisted = registered.filter((s) => !listed.includes(s));
  return (
    <Card header={<Text role="title" as="h3">Sports</Text>}>
      <Stack gap={12}>
        <Stack gap={4}>
          <Text role="caption" color="muted" as="span">
            Registered by the business
          </Text>
          {registered.length ? (
            <Flex gap={8} wrap>
              {registered.map((s) => (
                <Badge key={s} tone="accent">
                  {s}
                </Badge>
              ))}
            </Flex>
          ) : (
            <Text role="small" color="secondary" as="p">
              None registered on the business itself.
            </Text>
          )}
        </Stack>
        <Stack gap={4}>
          <Text role="caption" color="muted" as="span">
            Offered by its listings
          </Text>
          {listed.length ? (
            <Flex gap={8} wrap>
              {listed.map((s) => (
                <Badge key={s} tone="info">
                  {s}
                </Badge>
              ))}
            </Flex>
          ) : (
            <Text role="small" color="secondary" as="p">
              No listing has a sport yet.
            </Text>
          )}
        </Stack>
        {unlisted.length ? (
          <Text role="small" color="secondary" as="p">
            Registered but not offered by any listing: {unlisted.join(", ")}.
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

/** Owner, managers and staff, with contact details so a reviewer can reach them. */
export function BusinessPeopleCard({ overview }: { overview: BusinessOverviewResponse }) {
  return (
    <Card header={<Text role="title" as="h3">People</Text>}>
      {overview.people.length === 0 ? (
        <Text role="small" color="secondary" as="p">
          No one is linked to this business. It has no owner to notify.
        </Text>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Person</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Contact</Th>
              <Th>Since</Th>
            </Tr>
          </Thead>
          <Tbody>
            {overview.people.map((p) => (
              <Tr key={p.relationship_id}>
                <Td>
                  <Flex gap={8} align="center">
                    <Avatar
                      name={p.name ?? p.handle ?? "?"}
                      {...(p.avatar ? { src: p.avatar } : {})}
                      style={{ width: 28, height: 28 }}
                    />
                    <div>
                      <Link to={`/app/users/${p.user_id}`}>{p.name ?? p.user_id.slice(0, 8)}</Link>
                      {p.handle ? (
                        <Text role="caption" color="muted" as="div">
                          @{p.handle}
                        </Text>
                      ) : null}
                    </div>
                  </Flex>
                </Td>
                <Td>{formatLabel(p.relationship_type)}</Td>
                <Td>{formatLabel(p.status)}</Td>
                <Td>
                  {p.email ?? "â€”"}
                  {p.phone ? (
                    <Text role="caption" color="muted" as="div">
                      {p.phone}
                    </Text>
                  ) : null}
                </Td>
                <Td>{when(p.effective_from)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Card>
  );
}

/**
 * Payout account, commission and payouts â€” read only. None of these can be set
 * from HQ yet; the rules for them are a business decision still to be made.
 */
export function BusinessPaymentsCard({ overview }: { overview: BusinessOverviewResponse }) {
  const { payments, demand } = overview;
  return (
    <Card header={<Text role="title" as="h3">Payments & payouts</Text>}>
      <Stack gap={12}>
        {!payments.account_tracking_available ? (
          <Alert tone="warning" title="Payout accounts are not set up on this environment">
            The payout-account table (migration i36) is not applied to this
            database, so HQ cannot say whether this business can be paid.
          </Alert>
        ) : payments.account ? (
          <Text role="body" as="p">
            Payout account: {formatLabel(payments.account.provider)} {payments.account.account_ref}, linked{" "}
            {when(payments.account.linked_at)}
          </Text>
        ) : (
          <Alert tone="info" title="No payout account">
            This business has no payment-provider account, so money from its
            bookings cannot be routed to it yet.
          </Alert>
        )}
        <Text role="body" as="p">
          Commission rule:{" "}
          {payments.commission_rules.length === 0
            ? "none specific to this business"
            : payments.commission_rules
                .map((r) => `${formatLabel(r.status)} from ${when(r.effective_from)}`)
                .join("; ")}
        </Text>
        <Text role="body" as="p">
          Commission recorded on payments: {money(demand.commission_by_currency)}
        </Text>
        <Text role="body" as="p">
          Payouts:{" "}
          {payments.payouts_total === 0
            ? "none yet"
            : Object.entries(payments.payouts_by_status)
                .map(([status, b]) => `${b.count} ${formatLabel(status)} (${money(b.amount)})`)
                .join(", ")}
        </Text>
      </Stack>
    </Card>
  );
}

export function BusinessSafetyPanel({ overview }: { overview: BusinessOverviewResponse }) {
  return (
    <Stack gap={16}>
      <Stack gap={8}>
        <Text role="h2" as="h2">
          Reports against this business
        </Text>
        {overview.reports_against.length === 0 ? (
          <Text role="small" color="secondary" as="p">
            Nobody has reported this business.
          </Text>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Report</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Filed</Th>
              </Tr>
            </Thead>
            <Tbody>
              {overview.reports_against.map((r) => (
                <Tr key={r.id}>
                  <Td>
                    <Link to={`/app/trust-safety/reports/${r.id}`}>{r.id.slice(0, 8)}</Link>
                  </Td>
                  <Td>{formatLabel(r.report_type)}</Td>
                  <Td>{formatLabel(r.status)}</Td>
                  <Td>{when(r.submitted_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Stack>
      <Stack gap={8}>
        <Text role="h2" as="h2">
          Enforcement
        </Text>
        {overview.enforcement.length === 0 ? (
          <Text role="small" color="secondary" as="p">
            No enforcement actions on record.
          </Text>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Action</Th>
                <Th>Status</Th>
                <Th>From</Th>
                <Th>Reason</Th>
              </Tr>
            </Thead>
            <Tbody>
              {overview.enforcement.map((e) => (
                <Tr key={e.id}>
                  <Td>{formatLabel(e.action_type)}</Td>
                  <Td>{formatLabel(e.status)}</Td>
                  <Td>{when(e.effective_from)}</Td>
                  <Td>{e.reason ?? "â€”"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Stack>
    </Stack>
  );
}

export function BusinessTimelinePanel({ overview }: { overview: BusinessOverviewResponse }) {
  if (overview.timeline.length === 0) {
    return (
      <EmptyState
        title="No history yet"
        description="Every status change, document decision and edit made through the API is recorded here."
        reason="none"
      />
    );
  }
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>When</Th>
          <Th>What</Th>
          <Th>By</Th>
          <Th>Reason</Th>
        </Tr>
      </Thead>
      <Tbody>
        {overview.timeline.map((r) => (
          <Tr key={r.id}>
            <Td>{when(r.occurred_at)}</Td>
            <Td>{formatLabel(r.action.replace(/^business\./, "").replaceAll(".", " Â· "))}</Td>
            <Td>{r.actor?.name ?? "System"}</Td>
            <Td>{r.reason ?? "â€”"}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

export function BusinessNotesPanel(props: {
  notes: BusinessNoteDto[];
  busy: boolean;
  error: string | null;
  onAdd: (body: string) => Promise<boolean>;
  onPin: (note: BusinessNoteDto) => void;
}) {
  return <NotesPanel {...props} />;
}

/** i256: the venue's membership sales and holders, read-only in HQ. */
export function BusinessMembershipsCard({ businessId }: { businessId: string }) {
  const client = useApiClient();
  const api = useMemo(() => createBusinessesApi(client), [client]);
  const [data, setData] = useState<BusinessMembershipsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.getMemberships(businessId)
      .then((body) => { if (alive) setData(body); })
      .catch((e) => { if (alive) setError(apiErrorMessage(e)); });
    return () => { alive = false; };
  }, [api, businessId]);

  if (error) {
    return (
      <Card header={<Text role="title" as="h3">Memberships</Text>}>
        <Alert tone="danger">{error}</Alert>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card header={<Text role="title" as="h3">Memberships</Text>}>
        <Text role="body" color="muted" as="p">Loading...</Text>
      </Card>
    );
  }

  return (
    <Card header={<Text role="title" as="h3">Memberships</Text>}>
      <Stack gap={12}>
        <Text role="body" color="muted" as="p">
          {data.pending.length} waiting for payment, {data.memberships.length} active or closed.
        </Text>
        {data.memberships.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Shape</Th>
                <Th>Benefits</Th>
                <Th>Ends</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.memberships.slice(0, 25).map((m) => (
                <Tr key={`${m.format}-${m.id}`}>
                  <Td>{formatLabel(m.format)}</Td>
                  <Td>
                    {m.format === "member_rate" || m.format === "subscription"
                      ? `${m.discount_percent ?? 0}% off`
                      : `${m.sessions_left ?? 0} of ${m.total_sessions ?? 0} left`}
                  </Td>
                  <Td>
                    {when(m.valid_until)}
                    {m.format === "subscription" ? " · next payment due, paid in person" : " · does not renew"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <EmptyState title="No memberships sold yet" />
        )}
      </Stack>
    </Card>
  );
}

